import { twJoin } from 'tailwind-merge';

import { Badge, BadgeText } from '@/components/ui/badge';
import { HStack } from '@/components/ui/hstack';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { useHaptic } from '@/hooks/useHaptic';

export type SegmentTab<Key extends string = string> = {
  key: Key;
  title: string;
  /** Unread or pending items, drawn as a badge beside the title when above zero. */
  count?: number;
  /** Marks the section as having something new, as a dot. Says so to a screen reader too. */
  hasNewContent?: boolean;
};

type SegmentTabsProps<Key extends string> = {
  tabs: readonly SegmentTab<Key>[];
  selectedKey: Key;
  onSelect: (key: Key) => void;
  /** `compact` is short, left-aligned tabs that size to their titles, for a web list. */
  variant?: 'fill' | 'compact';
};

/**
 * The row that picks which list a tab root shows, in place of a swipeable pager.
 *
 * A pager holds several scroll views side by side, and iOS collapses a large title against
 * only one, so tab roots show one list at a time and switch it from here. The row renders at
 * the top of that list, so it scrolls away with the title rather than pinning beneath it.
 *
 * Every segment takes an equal share of the width and the full 48px height, so each is a
 * full-size target however short its title.
 */
export function SegmentTabs<Key extends string>({
  tabs,
  selectedKey,
  onSelect,
  variant = 'fill',
}: SegmentTabsProps<Key>) {
  const isCompact = variant === 'compact';

  const haptic = useHaptic();

  return (
    <HStack
      className={twJoin(
        'w-full border-b',
        isCompact ? 'border-outline-200' : 'border-outline-100 bg-background-0'
      )}
      accessibilityRole="tablist">
      {tabs.map((tab) => {
        const isSelected = tab.key === selectedKey;

        const hasCount = (tab.count ?? 0) > 0;

        const accessibilityLabel = [
          tab.title,
          hasCount ? `${tab.count} unread` : null,
          tab.hasNewContent ? 'new content available' : null,
        ]
          .filter(Boolean)
          .join(', ');

        return (
          <Pressable
            key={tab.key}
            className={twJoin(
              'items-center justify-center web:cursor-pointer',
              isCompact ? 'min-h-[44px] px-4' : 'min-h-[48px] flex-1'
            )}
            accessibilityRole="tab"
            accessibilityLabel={accessibilityLabel}
            accessibilityState={{ selected: isSelected }}
            onPress={() => {
              if (isSelected) {
                return;
              }

              haptic.selection();

              onSelect(tab.key);
            }}>
            <HStack space="sm" className="relative items-center py-3">
              <Text
                size="sm"
                className={twJoin(
                  'font-medium',
                  isSelected ? 'text-typography-900' : 'text-typography-600'
                )}>
                {tab.title}
              </Text>

              {hasCount ? (
                <Badge className={isSelected ? 'bg-primary-500' : 'bg-background-200'}>
                  <BadgeText className={isSelected ? 'text-typography-0' : 'text-primary-500'}>
                    {tab.count}
                  </BadgeText>
                </Badge>
              ) : null}

              {tab.hasNewContent ? (
                <View className="absolute -right-2.5 top-1.5 h-3 w-3 rounded-full bg-tertiary-400" />
              ) : null}
            </HStack>

            <View
              className={twJoin(
                'absolute bottom-0 left-0 right-0 h-0.5',
                isSelected ? 'bg-primary-500' : 'bg-transparent'
              )}
            />
          </Pressable>
        );
      })}
    </HStack>
  );
}
